import React, { createContext, useContext, useReducer, ReactNode, useMemo } from 'react';
import { CartItem, Cart } from '../types/cart';
import { Theme, Inventory, Plate, Dish, Business } from '../types';

interface CartContextType {
  cart: Cart;
  addToCart: (
    item: Theme | Inventory | Plate | Dish, 
    business: Business, 
    bookingDate?: string,
    selectedDishes?: Array<{ dishId: string; dishName: string; dishPrice: number; quantity: number }>,
    quantity?: number
  ) => void;
  removeFromCart: (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish') => void;
  updateQuantity: (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish', quantity: number) => void;
  updateBookingDate: (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish', bookingDate: string | undefined) => void;
  clearCart: () => void;
  getCartItemCount: () => number;
  getCartTotal: () => number;
  isInCart: (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish') => boolean;
  openCart: () => void;
  closeCart: () => void;
  isCartOpen: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

interface CartState {
  items: CartItem[];
}

type CartAction =
  | { type: 'ADD_TO_CART'; payload: CartItem }
  | { type: 'REMOVE_FROM_CART'; payload: { id: string; type: 'theme' | 'inventory' | 'plate' | 'dish' } }
  | { type: 'UPDATE_QUANTITY'; payload: { id: string; type: 'theme' | 'inventory' | 'plate' | 'dish'; quantity: number } }
  | { type: 'UPDATE_BOOKING_DATE'; payload: { id: string; type: 'theme' | 'inventory' | 'plate' | 'dish'; bookingDate: string | undefined } }
  | { type: 'CLEAR_CART' };

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItemIndex = state.items.findIndex(
        item => item.id === action.payload.id && item.type === action.payload.type
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...state.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + action.payload.quantity
        };
        return { items: updatedItems };
      } else {
        const newItems = [...state.items, action.payload];
        return { items: newItems };
      }
    }

    case 'REMOVE_FROM_CART': {
      return {
        items: state.items.filter(
          item => !(item.id === action.payload.id && item.type === action.payload.type)
        )
      };
    }

    case 'UPDATE_QUANTITY': {
      return {
        items: state.items.map(item =>
          item.id === action.payload.id && item.type === action.payload.type
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };
    }

    case 'UPDATE_BOOKING_DATE': {
      return {
        items: state.items.map(item =>
          item.id === action.payload.id && item.type === action.payload.type
            ? { ...item, bookingDate: action.payload.bookingDate }
            : item
        )
      };
    }

    case 'CLEAR_CART': {
      return { items: [] };
    }

    default:
      return state;
  }
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const [isCartOpen, setIsCartOpen] = React.useState(false);

  const addToCart = (
    item: Theme | Inventory | Plate | Dish, 
    business: Business, 
    bookingDate?: string,
    selectedDishes?: Array<{ dishId: string; dishName: string; dishPrice: number; quantity: number }>,
    quantity?: number
  ) => {
    // Check which properties exist
    const hasThemeId = 'themeId' in item || (item as any).themeId !== undefined;
    const hasInventoryId = 'inventoryId' in item || (item as any).inventoryId !== undefined;
    const hasDishId = 'dishId' in item || (item as any).dishId !== undefined;
    const hasPlateId = 'plateId' in item || (item as any).plateId !== undefined;
    
    let cartItem: CartItem;

    const vendorPhone = business.phoneNumber;
    const vendorMinOrderAmount = typeof (business as any).minOrderAmount === 'number'
      ? (business as any).minOrderAmount
      : undefined;
    
    if (hasThemeId) {
      // Theme item
      const theme = item as Theme;
      cartItem = {
        id: theme.themeId,
        type: 'theme',
        name: theme.themeName,
        description: theme.themeDescription,
        price: parseFloat(theme.priceRange.replace(/[^\d.]/g, '')) || 0,
        image: undefined, // Themes don't have single image
        businessId: business.businessId,
        businessName: business.businessName,
        quantity: 1,
        category: theme.themeCategory,
        themeId: theme.themeId,
        themeCategory: theme.themeCategory,
        bookingDate: bookingDate,
        vendorPhone,
        vendorMinOrderAmount
      };
    } else if (hasInventoryId) {
      // Inventory item
      const inventory = item as Inventory;
      cartItem = {
        id: inventory.inventoryId,
        type: 'inventory',
        name: inventory.inventoryName,
        description: inventory.inventoryDescription,
        price: inventory.price,
        image: undefined, // Inventory items don't have single image
        businessId: business.businessId,
        businessName: business.businessName,
        quantity: 1,
        category: inventory.inventoryCategory,
        inventoryId: inventory.inventoryId,
        inventoryCategory: inventory.inventoryCategory,
        bookingDate: bookingDate,
        vendorPhone,
        vendorMinOrderAmount
      };
    } else if (hasDishId) {
      // Dish item (check before plateId since both have dishName)
      const dish = item as Dish;
      cartItem = {
        id: dish.dishId,
        type: 'dish',
        name: dish.dishName,
        description: dish.dishDescription,
        price: dish.price,
        image: dish.dishImage,
        businessId: business.businessId,
        businessName: business.businessName,
        quantity: 1,
        category: 'Dish',
        dishId: dish.dishId,
        dishAvailabilityDates: dish.availabilityDates,
        bookingDate: bookingDate,
        vendorPhone,
        vendorMinOrderAmount
      };
    } else if (hasPlateId) {
      // Plate item - calculate total price including dishes
      const plate = item as Plate;
      const plateQuantity = quantity || 1; // Use provided quantity or default to 1
      const dishesTotal = selectedDishes?.reduce((sum, dish) => sum + dish.dishPrice * dish.quantity, 0) || 0;
      const pricePerPlate = plate.price + dishesTotal; // Price per plate including dishes
      
      cartItem = {
        id: plate.plateId,
        type: 'plate',
        name: plate.dishName,
        description: plate.dishDescription,
        price: pricePerPlate, // Price per plate (including dishes)
        image: plate.plateImage,
        businessId: business.businessId,
        businessName: business.businessName,
        quantity: plateQuantity, // Use the provided quantity
        category: 'Food',
        plateId: plate.plateId,
        dishType: plate.dishType,
        bookingDate: bookingDate,
        selectedDishes: selectedDishes || [],
        vendorPhone,
        vendorMinOrderAmount
      };
    } else {
      alert('Error: Could not add item to cart. Item type could not be determined.');
      return; // Invalid item type
    }

    dispatch({ type: 'ADD_TO_CART', payload: cartItem });
  };

  const removeFromCart = (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish') => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: { id: itemId, type: itemType } });
  };

  const updateQuantity = (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish', quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: itemId, type: itemType, quantity } });
  };

  const updateBookingDate = (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish', bookingDate: string | undefined) => {
    dispatch({ type: 'UPDATE_BOOKING_DATE', payload: { id: itemId, type: itemType, bookingDate } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getCartItemCount = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const isInCart = (itemId: string, itemType: 'theme' | 'inventory' | 'plate' | 'dish') => {
    return state.items.some(item => item.id === itemId && item.type === itemType);
  };

  const openCart = () => {
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  // Memoize cart object to prevent unnecessary re-renders
  const cart: Cart = useMemo(() => {
    const totalItems = state.items.reduce((total, item) => total + item.quantity, 0);
    const totalPrice = state.items.reduce((total, item) => total + (item.price * item.quantity), 0);
    return {
      items: state.items,
      totalItems: totalItems,
      totalPrice: totalPrice,
      businessId: state.items.length > 0 ? state.items[0].businessId : undefined
    };
  }, [state.items]);

  const value: CartContextType = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateBookingDate,
    clearCart,
    getCartItemCount,
    getCartTotal,
    isInCart,
    openCart,
    closeCart,
    isCartOpen
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
